/**
 * Enemy slots. Each slot is one prebuilt model and one preallocated record (GDD §10.3 pooling, §10.5 nothing
 * created in the loop). Slots are handed out as the concept's pools hand out models (concept L909–L910): the most
 * recently released first, otherwise the next never-used one. A model therefore keeps what it carries between uses,
 * as in the concept: a helicopter's rotor resumes at the angle it stopped.
 */
import { createEnemy, ENEMY_TYPES, type Enemy, type EnemyType } from '../sim/state.ts';

/**
 * Two sections' worth of each type at the §6.1 maxima (judgment call 21): only the remainder of one section can still
 * be ahead of the jet when the next spawns. The soak test in tests/unit/gameSim.test.ts proves the peak.
 */
export const POOL_SIZES: Readonly<Record<EnemyType, number>> = { ship: 20, heli: 20, fuel: 8, jet: 8 };

export interface TypePool {
  records: Enemy[];
  /** Released slots, a stack. */
  free: Int32Array;
  freeCount: number;
  /** Next never-used slot. */
  fresh: number;
  inUse: number;
  /** Most slots in use at once since the pool was made. */
  peak: number;
  /** Spawns refused because every slot was in use (must stay 0). */
  overflows: number;
}

export interface EnemyPool {
  types: Record<EnemyType, TypePool>;
  /** Main and tail rotor angles of each helicopter model (concept L1043: they accumulate on the model). */
  rotor: Float64Array;
  tail: Float64Array;
}

export function createEnemyPool(sizes: Readonly<Record<EnemyType, number>> = POOL_SIZES): EnemyPool {
  const types = {} as Record<EnemyType, TypePool>;
  for (const t of ENEMY_TYPES) {
    const n = sizes[t];
    types[t] = {
      records: Array.from({ length: n }, (_, i) => createEnemy(t, i)),
      free: new Int32Array(n),
      freeCount: 0,
      fresh: 0,
      inUse: 0,
      peak: 0,
      overflows: 0,
    };
  }
  return { types, rotor: new Float64Array(sizes.heli), tail: new Float64Array(sizes.heli) };
}

/** A slot's record, or null when every slot is in use (counted in `overflows`). */
export function acquireEnemy(pool: EnemyPool, type: EnemyType): Enemy | null {
  const p = pool.types[type];
  let slot: number;
  if (p.freeCount > 0) slot = p.free[--p.freeCount] ?? 0;
  else if (p.fresh < p.records.length) slot = p.fresh++;
  else {
    p.overflows++;
    return null;
  }
  p.inUse++;
  if (p.inUse > p.peak) p.peak = p.inUse;
  return p.records[slot] ?? null;
}

export function releaseEnemy(pool: EnemyPool, e: Enemy): void {
  const p = pool.types[e.type];
  p.free[p.freeCount++] = e.slot;
  p.inUse--;
}

/** Restarts the peak and overflow counts from the slots in use now (tests). */
export function resetPoolStats(pool: EnemyPool): void {
  for (const t of ENEMY_TYPES) {
    const p = pool.types[t];
    p.peak = p.inUse;
    p.overflows = 0;
  }
}
